/**
 * AS RECUSAS DO CADASTRO DE DDR (backend #506, 20/10/2026: "um número, uma linha").
 *
 * O caso: o (64) 3513-9000 do cliente 320 tinha duas linhas ativas (#6041 e #6050), e o Heitor apagou uma na mão. O
 * backend agora recusa antes de gravar, e o corpo da recusa diz QUEM já tem o número (`ddrId`, `clientId`), para a tela
 * abrir esse registro em vez de só dizer "não pode". Recusa que não aponta o caminho é recusa que o operador contorna
 * apagando coisa.
 *
 * Três corpos, pelo `code` (`helpers/numero-do-ddr.util.ts` do backend):
 *  - 409 `DDR_DUPLICADO` (create e update): message, ddrId, clientId, clientName, phoneNumber, status, numeroNormalizado;
 *  - 409 `DDR_DUPLICADO_EM_LOTE` (cadastro em faixa, tudo ou nada): message, total, conflitos[{id, clientId, phoneNumber, status}];
 *  - 400 `DDR_SEM_DIGITOS`: message.
 * O import CSV não muda: a repetição sai em `errors[]`, como as outras recusas dele.
 */

export type DdrQueJaTemONumero = { id: number; clientId: number; clientName: string | null; phoneNumber: string; status: string | null };

export type RecusaDoDdr =
  | { tipo: 'duplicado'; numeroDigitado: string; existente: DdrQueJaTemONumero; mensagem: string }
  | { tipo: 'lote'; total: number; conflitos: DdrQueJaTemONumero[]; mensagem: string }
  | { tipo: 'sem_digitos'; numeroDigitado: string; mensagem: string };

/** Lê a recusa do erro do axios; `null` quando não é uma das três (aí vale a mensagem de erro de sempre). */
export function lerRecusaDoDdr(e: any, numeroDigitado = ''): RecusaDoDdr | null {
  const status = e?.response?.status;
  const d = e?.response?.data;
  if (!d || typeof d !== 'object') return null;
  const mensagem = String(d.message || '');
  if (status === 409 && d.code === 'DDR_DUPLICADO' && Number.isFinite(Number(d.ddrId))) {
    return {
      tipo: 'duplicado',
      numeroDigitado,
      mensagem,
      existente: {
        id: Number(d.ddrId),
        clientId: Number(d.clientId),
        clientName: d.clientName ?? null,
        phoneNumber: String(d.phoneNumber ?? numeroDigitado),
        status: d.status ?? null,
      },
    };
  }
  if (status === 409 && d.code === 'DDR_DUPLICADO_EM_LOTE') {
    const conflitos = (Array.isArray(d.conflitos) ? d.conflitos : []).map((c: any) => ({
      id: Number(c?.id),
      clientId: Number(c?.clientId),
      clientName: null,
      phoneNumber: String(c?.phoneNumber ?? ''),
      status: c?.status ?? null,
    }));
    return { tipo: 'lote', total: Number(d.total) || conflitos.length, conflitos, mensagem };
  }
  if (status === 400 && d.code === 'DDR_SEM_DIGITOS') return { tipo: 'sem_digitos', numeroDigitado, mensagem };
  return null;
}

/** "de GRAVATAL (cliente #5)": o nome vem do corpo; sem ele, da lista de clientes da tela; sem os dois, só o id. */
export function donoDoDdr(existente: DdrQueJaTemONumero, nomeDoCliente?: (id: number) => string | null | undefined): string {
  const nome = existente.clientName || nomeDoCliente?.(existente.clientId) || null;
  return nome ? `${nome} (cliente #${existente.clientId})` : `cliente #${existente.clientId}`;
}

export function fraseDoDuplicado(r: Extract<RecusaDoDdr, { tipo: 'duplicado' }>, nomeDoCliente?: (id: number) => string | null | undefined): string {
  const e = r.existente;
  const situacao = e.status ? `, situação ${e.status}` : '';
  return `O número ${r.numeroDigitado || e.phoneNumber} já está cadastrado no DDR #${e.id}, de ${donoDoDdr(e, nomeDoCliente)}${situacao}. Nada foi gravado. Abra esse DDR para editá-lo ou transferi-lo, em vez de criar um segundo.`;
}

export function fraseDoLote(r: Extract<RecusaDoDdr, { tipo: 'lote' }>): string {
  const n = r.total;
  return `Nenhum DDR foi criado: ${n} número${n === 1 ? '' : 's'} da faixa já ${n === 1 ? 'está cadastrado' : 'estão cadastrados'}. A faixa é gravada inteira ou nada. Ajuste a faixa, ou edite os DDRs que já existem, e envie de novo.`;
}
